import allRoutes from "client/constants/allRoutes";
import { logout, updateProfile } from "client/slices/authSlice";
import { MainLogo } from "components/Images";
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { GlobeIcon1 } from "components/Icons";
import { updateTotalItem } from "client/slices/cartSlice";
import { useForm } from "react-hook-form";
import { baseApi } from "BaseAPI";
import apiPath from "client/constants/apiPath";
import { toast } from "react-toastify";
import ScrollToTop from "../ScrollTop/ScrollToTop";
import moment from "moment";
import { useBranding } from "context/BrandingProvider";
import { Button, Input, Modal } from "portal/components/ui";
import NotificationPanel from "./NotificationPanel";
import { useNotifications } from "portal/hooks/useNotifications";

const staffImgUrl = (filename) =>
  filename
    ? `${process.env.REACT_APP_STAFF_PROFILE_IMAGE_URL}/public/staff_profile_image/${filename}`
    : "";

const Header = ({ setSidebarOpen }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const { clinicName } = useBranding();
  const {
    notifications: notifList,
    unreadCount,
    filter: notifFilter,
    smartAlerts,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    changeFilter: changeNotifFilter,
    refresh: refreshNotifications,
  } = useNotifications();
  const dropdownRef = useRef(null);
  const [changepassportModal, setChangepassportModal] = useState(false);
  const [updateProfileModal, setUpdateProfileModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [image, setImage] = useState(null);
  const [disabled, setDisabled] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const logoutHandle = () => {
    dispatch(logout()); // root reducer clears localStorage + resets all state
    dispatch(updateTotalItem());
    navigate(allRoutes.landing);
  };
  const handleNavigation = () => {
    navigate(`${allRoutes?.portal}${allRoutes?.profileAccount}`);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const updateProfileHandle = async () => {
    try {
      const response = await baseApi.post(
        process.env.REACT_APP_PUBLIC_API_URL + "/" + apiPath.accouniInfo,
      );
      if (response?.rescode) {
        const {
          Image = "",
          FirstName = "",
          LastName = "",
          Phone = "",
        } = response?.data?.userProfile || {};

        reset({
          profile_image: staffImgUrl(Image),
          first_name: `${FirstName}`.trim(),
          last_name: `${LastName}`.trim(),
          mobile: Phone,
        });

        setProfileImage(staffImgUrl(Image));
        setUpdateProfileModal(true);
      }
    } catch (error) {
      toast.error(error?.message || error);
    }
  };

  const onSubmit = async (values) => {
    try {
      setDisabled(true);
      const response = await baseApi.post(
        process.env.REACT_APP_PUBLIC_API_URL + "/" + apiPath.updatePassword,
        {
          currentPassword: values.old_password,
          password: values.new_password,
        },
      );
      const { rescode = false, message } = response;

      if (rescode) {
        toast.success(message);
        setChangepassportModal(false);
      } else toast.error(message);
    } catch (error) {
      toast.error(error?.message || error || "Something went wrong");
    } finally {
      setDisabled(false);
    }
  };

  const onUpdateProfileSubmit = async (values) => {
    try {
      setDisabled(true);

      let base64Image = image;

      // If user uploaded a File object, convert it to base64
      if (image instanceof File) {
        base64Image = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result); // base64 string
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(image);
        });
      }
      // Prepare JSON payload
      const payload = {
        firstName: values.first_name,
        lastName: values.last_name,
        phone: values.mobile,
      };

      // Add profileImage only if it exists and not empty
      if (base64Image && base64Image.trim() !== "") {
        payload.profileImage = base64Image;
      }

      const response = await baseApi.post(
        `${process.env.REACT_APP_PUBLIC_API_URL}/${apiPath.updateProfile}`,
        payload,
      );

      const { rescode = false, message } = response;

      if (rescode) {
        dispatch(updateProfile(response?.data));
        toast.success(message);
        setUpdateProfileModal(false);
      } else {
        toast.error(message);
      }
    } catch (error) {
      toast.error(error?.message || error || "Something went wrong");
    } finally {
      setDisabled(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setProfileImage(URL.createObjectURL(file));
    }
  };
  return (
    <>
      <ScrollToTop />
      <header className="lg:hidden flex items-center justify-between px-6 py-3 bg-transparent h-[64px]">
        {/* Left Section */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen((prev) => !prev)}
            className="text-gray-500 hover:text-gray-700 focus:outline-none lg:hidden"
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 6H20M4 12H20M4 18H11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {/* Mobile logo */}
          <div className="lg:hidden">
            <Link to={allRoutes.home}>
              <img src={MainLogo} alt="Portal Logo" className="h-6" />
            </Link>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-3">
          {/* Date */}
          <span className="text-[13px] text-gray-400 hidden md:block">
            {moment().format("ddd, DD MMM YYYY")}
          </span>

          {/* Notification Bell */}
          <div className="relative">
            <button
              onClick={() => setNotificationOpen((st) => !st)}
              className="relative flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors focus:outline-none"
            >
              <svg
                className="w-[18px] h-[18px]"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 17H20L18.5951 15.5951C18.2141 15.2141 18 14.6973 18 14.1585V11C18 8.38757 16.3304 6.16509 14 5.34142V5C14 3.89543 13.1046 3 12 3C10.8954 3 10 3.89543 10 5V5.34142C7.66962 6.16509 6 8.38757 6 11V14.1585C6 14.6973 5.78595 15.2141 5.40493 15.5951L4 17H9M15 17V18C15 19.6569 13.6569 21 12 21C10.3431 21 9 19.6569 9 18V17M15 17H9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {notificationOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setNotificationOpen(false)}
                />
                <NotificationPanel
                  notifications={notifList}
                  smartAlerts={smartAlerts}
                  filter={notifFilter}
                  onChangeFilter={changeNotifFilter}
                  onMarkAsRead={markAsRead}
                  onMarkAllAsRead={markAllAsRead}
                  onDelete={deleteNotification}
                  onRefresh={refreshNotifications}
                  onClose={() => setNotificationOpen(false)}
                  onViewAll={() => {
                    setNotificationOpen(false);
                    navigate(allRoutes.notifications);
                  }}
                />
              </>
            )}
          </div>



        </div>
      </header>

      {/* Change Password Modal */}
      <Modal
        isOpen={changepassportModal}
        onClose={() => { setChangepassportModal(false); reset(); }}
        title="Change Password"
        size="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <Input
            label="Old Password"
            type="password"
            placeholder="Enter your old password"
            error={errors.old_password?.message}
            {...register("old_password", {
              required: "Old password is required",
            })}
          />
          <Input
            label="Password"
            type="password"
            placeholder="Enter your new password"
            error={errors.new_password?.message}
            {...register("new_password", {
              required: "Password is required",
              minLength: {
                value: 6,
                message: "Password must be at least 6 characters",
              },
              maxLength: {
                value: 15,
                message: "Password can't be more than 15 characters",
              },
              validate: (value) =>
                value !== watch("old_password") ||
                "New password must be different from old password",
            })}
          />
          <Input
            label="Confirm Password"
            type="password"
            placeholder="Confirm your password"
            error={errors.cpassword?.message}
            {...register("cpassword", {
              required: "Confirm Password is required",
              validate: (value) =>
                value === watch("new_password") ||
                "Confirm passwords must match",
            })}
          />
          <Button
            type="submit"
            disabled={disabled}
            loading={disabled}
            className="w-full"
          >
            Submit
          </Button>
        </form>
      </Modal>

      {/* Update Profile Modal */}
      <Modal
        isOpen={updateProfileModal}
        onClose={() => setUpdateProfileModal(false)}
        title="Update Profile"
        size="sm"
      >
        <form
          onSubmit={handleSubmit(onUpdateProfileSubmit)}
          noValidate
          className="space-y-4"
        >
          <div>
            <label className="text-body font-medium text-content-primary mb-1.5 block">
              Profile Image
            </label>
            <div className="mt-2 flex justify-center">
              <div className="relative">
                <label
                  htmlFor="profile_image"
                  className="block w-32 h-32 rounded-full bg-surface-hover cursor-pointer overflow-hidden border-2 border-surface-border"
                >
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-content-tertiary">
                      Upload
                    </div>
                  )}
                  <input
                    type="file"
                    id="profile_image"
                    accept="image/*"
                    {...register("profile_image")}
                    onChange={handleImageChange}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </label>
              </div>
            </div>
            {errors.profile_image && (
              <span className="text-danger-500 text-caption">
                {errors.profile_image.message}
              </span>
            )}
          </div>

          <Input
            label="First Name"
            type="text"
            placeholder="Enter your first name"
            error={errors.first_name?.message}
            {...register("first_name", {
              required: "First name is required",
              minLength: {
                value: 3,
                message: "First name must be at least 3 characters",
              },
            })}
          />
          <Input
            label="Last Name"
            type="text"
            placeholder="Enter your last name"
            error={errors.last_name?.message}
            {...register("last_name", {
              required: "Last name is required",
              minLength: {
                value: 3,
                message: "Last name must be at least 3 characters",
              },
            })}
          />
          <Input
            label="Mobile"
            type="text"
            placeholder="Enter your mobile number"
            error={errors.mobile?.message}
            {...register("mobile", {
              required: "Mobile number is required",
              minLength: {
                value: 6,
                message: "Mobile number must be at least 6 digits",
              },
              maxLength: {
                value: 10,
                message: "Mobile number cannot be more than 10 digits",
              },
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Mobile number must be 10 digits",
              },
            })}
          />
          <Button
            type="submit"
            disabled={disabled}
            loading={disabled}
            className="w-full"
          >
            Submit
          </Button>
        </form>
      </Modal>
    </>
  );
};

export default Header;
